import { isEmpty } from 'lodash/fp';
import { LocalizedPropertyObject } from '@mews/framework';
import {
    Product,
    ProductOptions,
    ProductPromotions,
    UpdateApiValue,
    NullableUpdateApiValue,
    ProductPricingCoProducts,
    FileUpdateValue,
    CommanderThunkAction,
    AsyncAction,
} from '@mews-commander/types';
import {
    MultipleProductPricingParameters,
    ProductChargingMode,
    ProductPricesResult,
    ProductPostingMode,
    Profile,
} from '@mews-commander/data-access';
import { createFetchAction, notEmpty } from '@mews-commander/utils';
import { setProducts } from '../ducks/products';
import { setProfiles } from '../ducks/profiles';
import prefix from '../prefix';
import { getServicePriceableProductIds } from '../selectors/products';

const PRODUCTS_FETCH = prefix('PRODUCTS_FETCH');
const PRODUCTS_UPDATE = prefix('PRODUCTS_UPDATE');
const PRODUCTS_ADD = prefix('PRODUCTS_ADD');
const PRODUCT_PRICES_FETCH = prefix('PRODUCT_PRICES_FETCH');

interface GetAllResponse {
    products: Array<Product>;
    profiles: Array<Profile>;
}

interface FetchProductsProps {
    enterpriseIds?: string[];
    serviceIds?: string[];
    productIds?: string[];
    extent?: {
        baseProducts?: boolean;
        profiles?: boolean;
    };
}

interface UpdateProductsProps {
    serviceId: string;
    productUpdates: Array<{
        productId: string;
        names?: NullableUpdateApiValue<LocalizedPropertyObject>;
        shortNames?: NullableUpdateApiValue<LocalizedPropertyObject>;
        externalNames?: NullableUpdateApiValue<LocalizedPropertyObject>;
        descriptions?: NullableUpdateApiValue<LocalizedPropertyObject>;
        productCategoryId?: NullableUpdateApiValue<string>;
        accountingCategoryId?: NullableUpdateApiValue<string>;
        rebateAccountingCategoryId?: NullableUpdateApiValue<string>;
        canceledItemAccountingCategoryId?: NullableUpdateApiValue<string>;
        ordering?: UpdateApiValue<number>;
        promotions?: NullableUpdateApiValue<ProductPromotions>;
        options?: NullableUpdateApiValue<ProductOptions>;
        price?: UpdateApiValue<ProductPricingCoProducts>;
        chargingMode?: NullableUpdateApiValue<ProductChargingMode>;
        postingMode?: NullableUpdateApiValue<ProductPostingMode>;
        image?: NullableUpdateApiValue<FileUpdateValue>;
    }>;
}

interface AddProductsProps {
    serviceId: string;
    products: Array<{
        names: LocalizedPropertyObject | null;
        shortNames: LocalizedPropertyObject | null;
        externalNames: LocalizedPropertyObject | null;
        descriptions: LocalizedPropertyObject | null;
        productCategoryId: string | null;
        accountingCategoryId: string | null;
        rebateAccountingCategoryId: string | null;
        canceledItemAccountingCategoryId: string | null;
        options: ProductOptions | null;
        promotions: ProductPromotions | null;
        pricing: ProductPricingCoProducts;
        chargingMode: ProductChargingMode | null;
        postingMode: ProductPostingMode | null;
        ordering: number | null;
    }>;
}

export const fetchProducts =
    (params: FetchProductsProps): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { container }) => {
        const promise = container()
            .productsComponent.getAll(params)
            .then(({ products, profiles }: GetAllResponse) => {
                if (notEmpty(products)) {
                    dispatch(setProducts(products));
                }

                if (notEmpty(profiles)) {
                    dispatch(setProfiles(profiles));
                }
            });

        return dispatch(createFetchAction(PRODUCTS_FETCH, promise));
    };

export const updateProducts =
    (params: UpdateProductsProps): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { container }) => {
        const promise = container()
            .productsComponent.update(params)
            .then(({ products, profiles }: GetAllResponse) => {
                if (notEmpty(products)) {
                    dispatch(setProducts(products));
                }

                if (notEmpty(profiles)) {
                    dispatch(setProfiles(profiles));
                }
            });
        return dispatch(createFetchAction(PRODUCTS_UPDATE, promise));
    };

export const addProducts =
    (params: AddProductsProps): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { container }) => {
        const promise = container()
            .productsComponent.add(params)
            .then(({ products }: { products: Array<Product> }) => {
                if (notEmpty(products)) {
                    dispatch(setProducts(products));
                    return products;
                }
            });
        return dispatch(createFetchAction(PRODUCTS_ADD, promise));
    };

type FetchServiceProductPricesParams = Omit<MultipleProductPricingParameters, 'productIds'>;

export const fetchServiceProductPrices =
    (params: FetchServiceProductPricesParams): CommanderThunkAction<AsyncAction<ProductPricesResult['prices']>> =>
    async (dispatch, getState, { repositories }) => {
        const { serviceId } = params;
        const productIds = getServicePriceableProductIds(getState(), { serviceId });

        const promise = isEmpty(productIds)
            ? Promise.resolve([])
            : repositories.products.getPrices({ ...params, productIds }).then(({ prices }) => prices);
        return dispatch(createFetchAction(PRODUCT_PRICES_FETCH, promise));
    };
